import React, { useState, useEffect } from "react";
import { Save } from "lucide-react";
import SlideOutPanel from "./SlideOutPanel";
import {
  TextInput,
  TextArea,
  FormSection,
  FieldRow,
  StrikeZoneGrid,
  HitterTypeSelect,
  Checkbox,
} from "../forms/FormFields";

const DEFAULT_FORM_DATA = {
  playerId: null,
  name: "",
  position: "",
  bats: "",
  hitterType: "",
  hotZones: [],
  coldZones: [],
  isStolenBaseThreat: false,
  isGoodWithTwoStrikes: false,
  isPullHeavy: false,
  isOppoHitter: false,
  isFirstPitchAggressive: false,
  isPatient: false,
  isFreeSwinger: false,
  isBuntThreat: false,
  likesVelocity: false,
  likesBreaking: false,
  strugglesVelocity: false,
  strugglesBreaking: false,
  writeup: "",
};

const EditBatterPanel = ({
  isOpen,
  onClose,
  onSubmit,
  editingPlayer,
}) => {
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);

  useEffect(() => {
    if (editingPlayer) {
      setFormData({
        playerId: editingPlayer.playerId || editingPlayer.id,
        name: editingPlayer.name || "",
        position: editingPlayer.position || "",
        bats: editingPlayer.bats || "",
        hitterType: editingPlayer.hitterType || "",
        hotZones: editingPlayer.hotZones || [],
        coldZones: editingPlayer.coldZones || [],
        isStolenBaseThreat: editingPlayer.isStolenBaseThreat || false,
        isGoodWithTwoStrikes: editingPlayer.isGoodWithTwoStrikes || false,
        isPullHeavy: editingPlayer.isPullHeavy || false,
        isOppoHitter: editingPlayer.isOppoHitter || false,
        isFirstPitchAggressive: editingPlayer.isFirstPitchAggressive || false,
        isPatient: editingPlayer.isPatient || false,
        isFreeSwinger: editingPlayer.isFreeSwinger || false,
        isBuntThreat: editingPlayer.isBuntThreat || false,
        likesVelocity: editingPlayer.likesVelocity || false,
        likesBreaking: editingPlayer.likesBreaking || false,
        strugglesVelocity: editingPlayer.strugglesVelocity || false,
        strugglesBreaking: editingPlayer.strugglesBreaking || false,
        writeup: editingPlayer.writeup || "",
      });
    } else {
      setFormData(DEFAULT_FORM_DATA);
    }
  }, [editingPlayer, isOpen]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
    onClose();
  };

  const handleClose = () => {
    setFormData(DEFAULT_FORM_DATA);
    onClose();
  };

  return (
    <SlideOutPanel
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Player"
      width="w-[700px]"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <FormSection title="Basic Information">
          <TextInput
            label="Name"
            value={formData.name}
            onChange={(v) => handleChange("name", v)}
            disabled
          />
          <FieldRow>
            <TextInput
              label="Position"
              value={formData.position}
              onChange={(v) => handleChange("position", v)}
              placeholder="e.g., SS, CF, 1B"
              className="flex-1"
            />
            <TextInput
              label="Bats"
              value={formData.bats}
              onChange={(v) => handleChange("bats", v)}
              placeholder="L, R, or S"
              className="w-24"
            />
          </FieldRow>
        </FormSection>

        <FormSection title="Hitting Tendencies">
          <HitterTypeSelect
            label="Hitter Type"
            value={formData.hitterType}
            onChange={(v) => handleChange("hitterType", v)}
          />
          <div className="flex gap-8 mt-6">
            <div className="flex-1">
              <StrikeZoneGrid
                label="Likes It (Hot Zones)"
                selectedZones={formData.hotZones}
                onChange={(zones) => handleChange("hotZones", zones)}
              />
            </div>
            <div className="flex-1">
              <StrikeZoneGrid
                label="Struggles With (Cold Zones)"
                selectedZones={formData.coldZones}
                onChange={(zones) => handleChange("coldZones", zones)}
              />
            </div>
          </div>
        </FormSection>

        <FormSection title="Approach">
          <div className="grid grid-cols-2 gap-x-8 gap-y-3">
            <Checkbox
              label="First Pitch Aggressive"
              checked={formData.isFirstPitchAggressive}
              onChange={(checked) => handleChange("isFirstPitchAggressive", checked)}
            />
            <Checkbox
              label="Good With Two Strikes"
              checked={formData.isGoodWithTwoStrikes}
              onChange={(checked) => handleChange("isGoodWithTwoStrikes", checked)}
            />
            <Checkbox
              label="Patient / Works Counts"
              checked={formData.isPatient}
              onChange={(checked) => handleChange("isPatient", checked)}
            />
            <Checkbox
              label="Free Swinger / Chases"
              checked={formData.isFreeSwinger}
              onChange={(checked) => handleChange("isFreeSwinger", checked)}
            />
          </div>
        </FormSection>

        <FormSection title="Batted Ball">
          <div className="grid grid-cols-2 gap-x-8 gap-y-3">
            <Checkbox
              label="Pull Heavy"
              checked={formData.isPullHeavy}
              onChange={(checked) => handleChange("isPullHeavy", checked)}
            />
            <Checkbox
              label="Opposite Field Hitter"
              checked={formData.isOppoHitter}
              onChange={(checked) => handleChange("isOppoHitter", checked)}
            />
          </div>
        </FormSection>

        <FormSection title="Pitch Preference">
          <div className="grid grid-cols-2 gap-x-8 gap-y-3">
            <Checkbox
              label="Likes Velocity"
              checked={formData.likesVelocity}
              onChange={(checked) => handleChange("likesVelocity", checked)}
            />
            <Checkbox
              label="Likes Breaking Balls"
              checked={formData.likesBreaking}
              onChange={(checked) => handleChange("likesBreaking", checked)}
            />
            <Checkbox
              label="Struggles vs Velocity"
              checked={formData.strugglesVelocity}
              onChange={(checked) => handleChange("strugglesVelocity", checked)}
            />
            <Checkbox
              label="Struggles vs Breaking"
              checked={formData.strugglesBreaking}
              onChange={(checked) => handleChange("strugglesBreaking", checked)}
            />
          </div>
        </FormSection>

        <FormSection title="Baserunning">
          <div className="grid grid-cols-2 gap-x-8 gap-y-3">
            <Checkbox
              label="Stolen Base Threat"
              checked={formData.isStolenBaseThreat}
              onChange={(checked) => handleChange("isStolenBaseThreat", checked)}
            />
            <Checkbox
              label="Bunt Threat"
              checked={formData.isBuntThreat}
              onChange={(checked) => handleChange("isBuntThreat", checked)}
            />
          </div>
        </FormSection>

        <FormSection title="Write-up">
          <TextArea
            label="Scouting Notes"
            value={formData.writeup}
            onChange={(v) => handleChange("writeup", v)}
            rows={5}
            placeholder="Enter scouting notes, observations, strengths, weaknesses..."
          />
        </FormSection>

        <div className="flex gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center gap-2"
          >
            <Save size={16} />
            Save Player
          </button>
        </div>
      </form>
    </SlideOutPanel>
  );
};

export default EditBatterPanel;
